import { type DocNode } from '@atlaskit/adf-schema/doc';
import { type LabelStack } from '@atlaskit/react-ufo/interaction-context';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import type {
	AiFeatureRequestEnvelopeRovoConvoStarterRecommendationRequestNew,
	AIFeatureResponseEnvelopeRovoConvoStarterRecommendationResponseNew,
} from '@atlassian/convo-ai-api';

import { ASSISTANCE_SERVICE_ERROR_CODES } from '../../common/constants/public';
import { isApiError } from '../../common/utils/api-error';

import { type AgentAction } from './agent-actions/types';
import {
	type BulkMessageActionPayload,
	type BulkMessageActionResponse,
	type MessageAction,
	type MessageActionPayload,
	type MessageActionPayloadContext,
	type MessageActionResponse,
} from './message-actions/types';
import { type PluginInvocationMessage } from './plugins';

/**
 * Represents a parsed SSE message with metadata from the standard W3C SSE format.
 * When includeMetadata is true, readSSEStream yields this type.
 * - eventType: from SSE `event:` line
 * - id: from SSE `id:` line (used e.g. for Last-Event-ID / reconnect).
 * - data: parsed JSON from SSE `data:` line.
 * The __isSSEWrapper discriminator ensures this type is never confused with StreamMessage.
 */
export type ParsedSSEMessage<T> = {
	/** Discriminator field - guarantees this is an SSE wrapper, not a StreamMessage */
	__isSSEWrapper: true;
	eventType?: string;
	data: T;
	id?: string;
};

/**
 * Type guard to check if a message is a ParsedSSEMessage wrapper.
 * Uses the __isSSEWrapper discriminator to avoid false positives with StreamMessage.
 */
export const isSSEMessage = <T>(message: unknown): message is ParsedSSEMessage<T> => {
	return (
		message !== null &&
		typeof message === 'object' &&
		'__isSSEWrapper' in message &&
		(message as ParsedSSEMessage<T>).__isSSEWrapper === true
	);
};

export const CONVERSATION_CHANNEL_NOT_FOUND_ERROR = 'convoai.rest.not.found' as const;

export const ACCEPTABLE_USE_VIOLATIONS_ERROR = 'ACCEPTABLE_USE_VIOLATIONS' as const;

export type MessageFileId = {
	id: string;
	name: string;
};

export type WebSearchConfigurationResponse = {
	resourceAri: string;
	enabled: boolean;
};

/**
 * Response tone the user selects for how Rovo communicates. These values are
 * owned by the frontend and are the source of truth for the response-style
 * settings surface; they must match the values the backend persists.
 */
export type RovoResponseTone = 'rovo_default' | 'professional' | 'creative' | 'empathetic';

/** A per-request Writing Styles override for a Chat response. */
export type WritingStyleContext = {
	styleId: RovoResponseTone;
};

/** How long Rovo's responses should be. */
export type RovoResponseLength = 'shorter' | 'neutral' | 'longer';

/** How much structure Rovo's responses should use. */
export type RovoResponseStructure = 'less_structured' | 'neutral' | 'more_structured';

/** How frequently Rovo should use emojis in responses. */
export type RovoResponseEmojis = 'none' | 'neutral' | 'more';

/** Whether Rovo puts a TL;DR / key-facts summary on top of responses. */
export type RovoResponseTldr = 'enabled' | 'disabled';

/**
 * Runtime companions to the response-style value types, in display order. UI
 * surfaces map over these to render the options; keep each in sync with its type.
 */
export const RESPONSE_TONES: readonly RovoResponseTone[] = [
	'rovo_default',
	'professional',
	'creative',
	'empathetic',
];

export const RESPONSE_LENGTHS: readonly RovoResponseLength[] = ['shorter', 'neutral', 'longer'];

export const RESPONSE_STRUCTURES: readonly RovoResponseStructure[] = [
	'less_structured',
	'neutral',
	'more_structured',
];

export const RESPONSE_EMOJIS: readonly RovoResponseEmojis[] = ['none', 'neutral', 'more'];

export const RESPONSE_TLDR_OPTIONS: readonly RovoResponseTldr[] = ['enabled', 'disabled'];

/** Atlassian app where a writing style can be applied. */
export type WritingStyleApp = 'jira' | 'confluence' | 'slack';

/** Associates an app with the writing style it should use. */
export type WritingStyleBinding = {
	app: WritingStyleApp;
	styleId: string;
};

/**
 * The user's writing-style selection. Supplied app bindings are merged with
 * persisted bindings; bindings for the same app are replaced.
 */
export type WritingStylePreferences = {
	defaultStyleId: string;
	/** Omitted bindings are equivalent to an empty list. */
	bindings?: WritingStyleBinding[];
};

/** Writing-style preferences with optional API fields resolved for UI consumers. */
export type ResolvedWritingStylePreferences = Omit<WritingStylePreferences, 'bindings'> & {
	bindings: WritingStyleBinding[];
};

/** Response-style identifiers persisted by the user settings API. */
export type RovoResponseStyleId =
	| 'rovo-default'
	| 'preset:professional'
	| 'preset:creative'
	| 'preset:empathetic';

export type RovoResponseLengthPreference = 'none' | 'shorter' | 'longer';

export type RovoHeadingsAndListsPreference = 'none' | 'fewer' | 'more';

export type RovoEmojisPreference = 'none' | 'no-emojis' | 'more-emojis';

export type RovoTldrPreference = 'no-tldr' | 'include-tldr';

export type RovoResponseStyleCharacteristics = {
	length?: RovoResponseLengthPreference;
	headingsAndLists?: RovoHeadingsAndListsPreference;
	emojis?: RovoEmojisPreference;
	tldr?: RovoTldrPreference;
};

export type RovoResponseStylePreferences = {
	defaultStyleId: RovoResponseStyleId;
	characteristics?: RovoResponseStyleCharacteristics;
};

export type RovoUserSettings = {
	/**
	 * Whether the user allows Rovo to save memories from conversations
	 */
	allowMemory: boolean;
	/**
	 * Whether the user allows Rovo to reference past conversations
	 */
	allowConversationReference: boolean;
	/**
	 * Whether the user has opted in to having their chat messages stored.
	 * Omitted by the backend (undefined) when the user is not eligible for this preference.
	 */
	storeUserMessages?: boolean;
	/**
	 * Whether the user is eligible to set storeUserMessages.
	 * BE-computed via the `ai_mate_show_store_messages` feature gate.
	 */
	storeUserMessagesEligible?: boolean;
	/**
	 * Whether web search should be enabled by default for the user.
	 */
	webSearchDefaultEnabled?: boolean;
	/** The response style persisted for this user. */
	responseStylePreferences?: RovoResponseStylePreferences;
	/**
	 * The user's writing-style preferences. Absent until the user saves a
	 * preference block.
	 */
	writingStylePreferences?: WritingStylePreferences | null;
};

/**
 * Represents a memory reference (can be AGENT or CONVERSATION)
 */
export type RovoMemoryReference = {
	referenceType: 'AGENT' | 'CONVERSATION';
	referenceId: string;
};

/**
 * Represents a memory in Rovo
 */
export type RovoMemory = {
	/**
	 * Unique identifier for the memory
	 */
	id: string;
	/**
	 * The channel ID associated with the memory
	 */
	channelId: string;
	/**
	 * The user ID who created the memory
	 */
	userId: string;
	/**
	 * Reference to the agent or conversation
	 */
	reference: RovoMemoryReference;
	/**
	 * The content of the memory
	 */
	content: string;
	/**
	 * When the memory was created
	 */
	createdAt: string;
	/**
	 * When the memory expires
	 */
	expiresAt: string;
};

/**
 * Response from the get memories API
 */
export type RovoMemoriesResponse = RovoMemory[];

/**
 * Request body for the POST /memories/memory-edits API.
 */
export type MemoryEditRequest = {
	content: string;
};

/**
 * Request body for the POST /memories/create API.
 */
export type CreateMemoryRequest = {
	content: string;
};

/**
 * Response from the POST /memories/memory-edits API.
 */
export type MemoryEditPromptResponse = {
	id: string;
	content: string;
	createdAt: string;
	metadata?: Record<string, string>;
};

/**
 * Response from the GET /memories/blurb API.
 * Contains a human-readable narrative summary of the user's implicit identity
 * (role, OKRs, projects, tools) built from TWG activity and conversational memory.
 * `content` is null when there is insufficient signal to generate one.
 */
export type RovoMemoryBlurbResponse = {
	content: string | null;
};

/**
 * A single pending memory edit suggested by the user.
 */
export type RovoMemoryEdit = {
	/**
	 * Unique identifier for the memory edit
	 */
	id: string;
	/**
	 * The suggested edit content
	 */
	content: string;
	/**
	 * When the edit was created
	 */
	createdAt: string;
	/**
	 * Optional metadata associated with the edit
	 */
	metadata: Record<string, unknown> | null;
};

/**
 * Response from the GET /memories/memory-edits API.
 * Paginated list of pending memory edits.
 */
export type RovoMemoryEditsResponse = {
	items: RovoMemoryEdit[];
	nextPageKey: string | null;
};

/**
 * Response from the GET /memories/memory-edits/count API.
 */
export type RovoMemoryEditsCountResponse = {
	count: number;
};

export enum PromptCategory {
	ANALYZE = 'Analyze',
	BRAINSTORM = 'Brainstorm',
	REVIEW = 'Review',
	SUMMARIZE = 'Summarize',
	CREATE = 'Create',
	EXECUTE = 'Execute',
	FIND = 'Find',
	LEARN = 'Learn',
}

export enum PromptRole {
	GENERAL = 'General',
	ENGINEERING = 'Engineering',
	PRODUCT = 'Product',
	PROGRAM_MANAGEMENT = 'Program Management',
	DESIGN = 'Design',
	MARKETING = 'Marketing',
	IT_OPS = 'IT Ops',
	SALES = 'Sales',
	CUSTOMER_SUPPORT = 'Customer Support',
	HELPDESK_SUPPORT = 'Helpdesk Support',
	SECURITY = 'Security',
}

export type SimpleUserProfile = {
	id: string;
	ari: string;
	name: string;
	picture?: string;
};

export type GetPromptResponse = {
	id: string;
	prompt: string;
	title: string;
	useCase?: PromptCategory;
	roles?: PromptRole[];
	creator: SimpleUserProfile;
	sharedWith?: SimpleUserProfile[];
	isFavorited: boolean;
	appIds?: string[];
	isSystemPrompt?: boolean;
	isPrivate?: boolean;
	inactive?: boolean;
	favoriteCount?: number;
	createdAt?: number;
	inLibrary?: boolean;
};

export type GetAllPromptsResponse = {
	prompts: GetPromptResponse[];
};

export type CreatePromptRequest = {
	title: string;
	prompt: string;
	useCase?: PromptCategory;
	roles?: PromptRole[];
	appIds?: string[];
};

export type UpdatePromptRequest = {
	title?: string;
	prompt?: string;
	useCase?: PromptCategory;
	roles?: PromptRole[];
	appIds?: string[];
	isPrivate?: boolean;
	isFavorited?: boolean;
};

export type AddPromptsToLibraryRequest = {
	shareIds: string[];
};

export type FavoritePromptItem = {
	shareId: string;
	isFavorite: boolean;
};

export type FavoritePromptsRequest = {
	prompts: FavoritePromptItem[];
};

export type BulkUpdatePrivacyItem = {
	shareId: string;
	isPrivate: boolean;
};

export type BulkUpdatePrivacyRequest = {
	prompts: BulkUpdatePrivacyItem[];
};

export type BulkUpdatePrivacyResult = {
	shareId: string;
	success: boolean;
	isPrivate: boolean | null;
	error: string | null;
};

export type BulkUpdatePrivacyResponse = {
	results: BulkUpdatePrivacyResult[];
};

export type BulkGetPromptsRequest = {
	shareIds: string[];
};

export type BulkGetPromptResult = {
	shareId: string;
	success: boolean;
	prompt: GetPromptResponse | null;
	error: string | null;
};

export type BulkGetPromptsResponse = {
	results: BulkGetPromptResult[];
};

export type ConversationContent = {
	id: string;
	content_display_name: string;
	content_ari: string;
	created_at: string;
	action_conversation_id: string;
	invocation_id: string;
	action_status: string;
	action_key: string;
	owner: string;
};

export type BrowserUrlDisplayContextReference = {
	type: 'BROWSER_URL';
	browser_url: string;
};

export type HumanMessageDisplayContext = {
	references: BrowserUrlDisplayContextReference[];
};

export type HumanMessage = {
	role: 'HUMAN';
	content: string | DocNode;
	id: string;
	time_created: string;
	user_ari: string;
	plugin_invocations: PluginInvocationMessage[];
	isFromSeededConversation?: boolean;
	requestId?: string;
	file_uploads?: MessageFileId[];
	parent_message_id?: string | null; // null for legacy
	display_context?: HumanMessageDisplayContext | null;
};

export type AssistantMessage = Message & {
	role: 'ASSISTANT';
};

export type ContentType = 'slide' | 'live doc' | 'whiteboard' | 'database';

export const SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE = 'PROMPT_TILE';

/**
 * Marker value the backend sets on `SuggestedPromptTile.source_content_type` for
 * the third-party ("3P") prompt tile. The frontend identifies the 3P card by this
 * marker rather than by slot position, because personalized tiles get re-packed
 * (see `buildFilteredPersonalizedPrompts`) and position is not stable.
 */
export const THIRD_PARTY_PROMPT_SOURCE_CONTENT_TYPE = 'third-party';

/**
 * A third-party knowledge-source connector attached to the 3P prompt tile, ranked
 * by most-viewed 3P links (connected or not). Sub-fields are a pass-through of the
 * backend connector record.
 */
export type ThirdPartyConnector = {
	/**
	 * Knowledge-namespace product key (e.g. `slack`, `google_drive`). This is NOT the
	 * `ProductKeys3P` vocabulary that `RovoLogoForAppSource` resolves logos by: a connector
	 * can arrive in its smartlinks flavour (`figma_sl`), which that registry does not carry.
	 * Normalise it before resolving a logo.
	 */
	source: string;
	type: string;
	integration_id: string;
	outbound_auth_url: string;
	is_user_oauthed: boolean;
	provider_id: string;
	datasource_id: string;
};

export type SuggestedPromptTile = {
	title: string;
	/**
	 * Condensed plain-text label for the same creation task (<= 25 chars). Only returned by the
	 * backend when the blank-object experiment is on; omitted otherwise. Serialized as camelCase
	 * `shortTitle` on the wire (unlike `content_type`).
	 */
	shortTitle?: string;
	text: string;
	content_type: ContentType;
	icon: string;
	/**
	 * Backend-derived source marker. Equals `THIRD_PARTY_PROMPT_SOURCE_CONTENT_TYPE`
	 * for the 3P prompt tile; other tiles may carry other source markers or omit it.
	 */
	source_content_type?: string;
	/** Present only on the 3P tile. Ranked, connected-or-not, capped by the backend. */
	third_party_connectors?: ThirdPartyConnector[];
};

export type SuggestedPromptTileStreamMessage = {
	type: typeof SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE;
	message: SuggestedPromptTile;
};

export type SuggestedPromptsResponse = {
	ai_feature_output: {
		prompt_tiles: SuggestedPromptTile[];
	};
};

// ************************************************************************************************
// Response data & metadata
// Sent by POST:/chat endpoint
// ************************************************************************************************

export type ModelUsage = {
	total_tokens: number;
	prompt_tokens: number;
	completion_tokens: number;
	request_count: number;
	total_cost: number;
	duration: number;
};

export type Usage = {
	model_usage: {
		[key: string]: ModelUsage;
	};
	total: ModelUsage;
};

export type Durations = {
	events: {
		[key: string]: number;
	};
	total: number;
};

export type DocSegment = {
	content: string;
	token_count: number;
	length: number;
	parent_ari: string;
	similarity_score: number;
};

export type DocSegments = Array<DocSegment>;

export type AgentResponse = {
	message: AssistantMessage;
	human_message?: HumanMessage;
	usage_data?: Usage;
	durations_data?: Durations;
};

// ************************************************************************************************
//  Streaming Messages
// Sent by POST:/chat/stream endpoint
// ************************************************************************************************

export type StreamResponse = {
	type: 'FINAL_RESPONSE';
	message: AgentResponse;
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
	streamingComplete?: boolean;
};

export type StreamInteractionConclusion = {
	type: 'INTERACTION_CONCLUSION';
	message: {
		outcome: 'SUCCEEDED' | 'FAILED';
	};
	millisOffset?: number;
	metadata?: null | {
		request_id?: string | null;
		message_id?: string | null;
	};
};

export type StreamError = {
	type: 'ERROR';
	message: {
		content: string;
		status_code?: number;
		message_template?: string;
	};
	millisOffset: number;
	metadata: null | {
		error_message?: string;
		request_id?: string;
		timeout?: number;
	};
};

export type StreamReconnectSupportPart = {
	type: 'RECONNECT_SUPPORTED';
};

export type StreamHeartbeat = {
	type: 'HEART_BEAT';
	content: string;
};

export type StreamAnswerPart = {
	type: 'ANSWER_PART';
	message: { content: string; role: 'ASSISTANT' };
	millisOffset: number;
	metadata?: {
		run_id?: string;
		request_id?: string;
	};
};

export type EntityAnnotation = {
	type: 'Person' | 'InferredProject';
	mention: string;
	start: number;
	end: number;
	ari: string;
	canonicalName: string;
	confidenceScore: number;
};

export type EntityAnnotationsMessage = {
	sentence: string;
	entities: readonly EntityAnnotation[];
};

type RawEntityAnnotation = {
	type: 'person' | 'Person' | 'inferred_project' | 'InferredProject';
	mention: string;
	start: number;
	end: number;
	ari: string;
	canonical_name: string;
	confidence_score: number;
};

type RawEntityAnnotationsMessage = {
	sentence: string;
	entities: readonly RawEntityAnnotation[];
};

export type StreamEntityAnnotations = {
	type: 'ENTITY_ANNOTATIONS';
	message: RawEntityAnnotationsMessage;
	millisOffset: number;
	metadata?: {
		run_id?: string;
		request_id?: string;
	};
};

const entityTypeMap = {
	person: 'Person',
	Person: 'Person',
	inferred_project: 'InferredProject',
	InferredProject: 'InferredProject',
} as const;

export const normalizeEntityAnnotationsMessage = (
	message: RawEntityAnnotationsMessage,
): EntityAnnotationsMessage => ({
	sentence: message.sentence,
	entities: message.entities.map((entity) => ({
		type: entityTypeMap[entity.type],
		mention: entity.mention,
		start: entity.start,
		end: entity.end,
		ari: entity.ari,
		canonicalName: entity.canonical_name,
		confidenceScore: entity.confidence_score,
	})),
});

export type StreamReasoningPart = {
	type: 'REASONING_PART';
	message: { content: string; role: 'ASSISTANT'; stepId: string };
	millisOffset: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamTrace = {
	type: 'TRACE';
	message: { content: string; message_template?: string };
	millisOffset: number;
	metadata?: {
		run_id: string;
		request_id: string;
		plugin_name?: string;
		plugin_input?: string;
	};
};

export type StreamMemoryUpdateTrace = {
	type: 'TRACE';
	message: { content: string; message_template: 'MEMORY_UPDATE' };
	millisOffset: number;
	metadata?: {
		request_id: string;
		plugin_name?: string;
		plugin_input?: string;
	};
};

export type StreamResearchTrace = {
	type: 'TRACE';
	message: { message_template: 'RESEARCH'; content: string };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type AnalysingQueryTrace = {
	type: 'TRACE';
	message: { message_template: 'ANALYSING_QUERY'; content: string };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type WritingTrace = {
	type: 'TRACE';
	message: { message_template: 'WRITING'; content: string; user_query?: string | null };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepContent = {
	type: 'TRACE';
	message: { message_template: 'RESEARCH_STEP'; content: string; stepId: string; roundId: number };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepTitle = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_TITLE';
		title: string;
		stepId: string;
		roundId: number;
		toolIcon?: string;
		toolName?: string;
	};
};

export type StreamResearchPlan = {
	type: 'TRACE';
	message: { message_template: 'RESEARCH_PLAN'; content: string; roundId: number };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type TodoItem = {
	id: string;
	content: string;
	status: string;
	priority: string;
	active_form: string;
};

export type StreamTodoSnapshot = {
	type: 'TODO_SNAPSHOT';
	message: { todos: TodoItem[] };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamTaskStatus = {
	type: 'TASK_STATUS';
	message: {
		task_id: string;
		title: string;
		status: ApiTaskStatus;
		message_id: string | null;
		completion_message_id: string | null;
		created_at: string;
		started_at: string | null;
		updated_at: string;
		completed_at: string | null;
		current_message_id: string | null;
		execution_segments: Record<string, string> | null;
	};
	millisOffset: number;
	metadata?: { request_id: string | null };
	sequence_id?: string;
};

export type StreamResearchStepTitleShort = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_TITLE_SHORT';
		title: string;
		stepId: string;
		roundId: number;
		totalRoundId?: number | null;
		formulated_query?: string | null;
		sources?: Sources | null;
		toolIcon?: string;
		toolName?: string;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepSource = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_SOURCE';
		sources: Source[];
		stepId: string;
		roundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepCompleted = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_COMPLETE';
		stepId: string;
		roundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepIcons = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_ICONS';
		content: string;
		products: string[];
		stepId: string;
		roundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepDocument = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_DOCUMENT';
		content: string;
		title: string;
		stepId: string;
		roundId: number;
		product: string;
		url: string;
		toolName: string;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStart = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_START';
		totalRoundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamSkillStart = {
	type: 'TRACE';
	message: {
		message_template: 'SKILL_START';
		skill_status_metadata: { aliases: string[] };
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamSkillEnd = {
	type: 'TRACE';
	message: {
		message_template: 'SKILL_END';
		skill_status_metadata: { aliases: string[] };
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamFastModeResponseAvailable = {
	type: 'TRACE';
	message: {
		message_template: 'FAST_MODE_RESPONSE_AVAILABLE';
		content: string;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamFastModeEnabled = {
	type: 'TRACE';
	message: {
		message_template: 'FAST_MODE_ENABLED';
		content: string;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamRemoveAnswerNow = {
	type: 'TRACE';
	message: {
		message_template: 'REMOVE_ANSWER_NOW';
		content: string;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type FollowUpObject = {
	type: 'SYNTHETIC' | 'LLM_GENERATED';
	text: string;
};

export type StreamFollowUp = {
	type: 'FOLLOW_UP_QUERIES';
	message: {
		follow_ups: FollowUpObject[] | null;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamAgenticUi = {
	type?: 'AGENTIC_UI';
	message: {
		content: unknown[];
	};
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamActionPart = {
	type: 'ACTION_PART';
	message: MessageAction;
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamActionClassification = {
	type: 'ACTION_CLASSIFICATION';
	message: {
		action_key: string;
		content: string;
		invocationId?: string;
		metadata?: {
			promptVersion?: string;
			contentId?: string;
		};
	};
	millisOffset: number;
};

export type StreamActionComplete = {
	type: 'ACTION_COMPLETE';
	message: MessageAction;
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamActionPrepare = Omit<StreamActionPart, 'type'> & {
	type: 'ACTION_PREPARE';
};

export type StreamPromptRedaction = {
	type: 'PROMPT_REDACTION';
	message: {
		original_prompt: string;
		redacted_prompt: string;
	};
	millisOffset: number;
	metadata: { request_id: string | null };
};

export type StreamPromptBlock = {
	type: 'PROMPT_BLOCKED';
	message: {
		message_template: string;
		content: string;
		status_code: number;
	};
	millisOffset: number;
	metadata: { error_message: string; request_id: string | null };
};

export type StreamResponseRedaction = {
	type: 'RESPONSE_REDACTION';
	message: {
		original_response: string;
		redacted_response: string;
	};
	millisOffset: number;
	metadata: { request_id: string | null } | null;
};

export type StreamResponseBlock = {
	type: 'RESPONSE_BLOCKED';
	message: {
		message_template: string;
		content: string;
		status_code: number;
	};
	millisOffset: number;
	metadata: { error_message: string; request_id: string | null };
};

export type StreamAttachmentBlock = {
	type: 'ATTACHMENT_BLOCKED';
	message: {
		message_template: string;
		content: string;
		status_code: number;
	};
	millisOffset: number;
	metadata: { error_message: string; request_id: string | null };
};

export type StreamMessage =
	| StreamTrace
	| StreamResearchTrace
	| StreamResearchStepContent
	| StreamResearchStepTitle
	| StreamResearchStepTitleShort
	| StreamResearchStepSource
	| StreamResearchStepCompleted
	| StreamResearchStepIcons
	| StreamResearchStepDocument
	| StreamResearchPlan
	| AnalysingQueryTrace
	| WritingTrace
	| StreamAnswerPart
	| StreamEntityAnnotations
	| StreamReasoningPart
	| StreamResponse
	| StreamInteractionConclusion
	| StreamError
	| StreamFollowUp
	| StreamActionPart
	| StreamActionPrepare
	| StreamActionComplete
	| StreamActionClassification
	| ConversationChannelDataMessage
	| ScopeDeterminedMessage
	| SpaceSourceRecommendationsMessage
	| StreamReconnectSupportPart
	| StreamHeartbeat
	| StreamMemoryUpdateTrace
	| StreamTodoSnapshot
	| StreamTaskStatus
	| StreamPromptRedaction
	| StreamPromptBlock
	| StreamResponseRedaction
	| StreamResponseBlock
	| StreamAttachmentBlock;

export type StreamMessageWithMessage = Extract<StreamMessage, { message: unknown }> & {
	message: { data?: unknown };
};

/*
NLCreate Stream message types
*/
export type NLCreateAgentPayload = {
	latest_user_message: string;
	draft_agent: {
		conversation_starters: string[];
		description: string;
		instructions: string;
		name: string;
	};
	conversation_history: {
		content: string;
		role: 'HUMAN' | 'ASSISTANT';
	}[];
	mime_type: 'text/adf';
};

export type NLCreateAgentStreamDraftAgent = {
	type: 'DRAFT_AGENT';
	message: {
		name: string;
		description: string;
		instructions: string;
		conversation_starters: string[];
	};
	metadata: {
		request_id: string;
	};
};
export type NLCreateAgentStreamAnswerPart = {
	type: 'ANSWER_PART';
	message: {
		content: string;
	};
	metadata: {
		request_id: string;
	};
};

export type NLCreateAgentStreamFinalResponse = {
	type: 'FINAL_RESPONSE';
	message: {
		content: string;
	};
	metadata: {
		request_id: string;
	};
};

export type NLCreateAgentStreamError = {
	type: 'ERROR';
	message: {
		message_template: string;
		content: string;
		status_code: number;
	};
	metadata: {
		request_id: string;
	};
};
export type NLCreateAgentStreamMessage =
	| NLCreateAgentStreamDraftAgent
	| NLCreateAgentStreamAnswerPart
	| NLCreateAgentStreamFinalResponse
	| NLCreateAgentStreamError;

// ************************************************************************************************
// Other types
// ************************************************************************************************

// All responses should be converted into StreamResponse
// - AssistantMessage -> StreamResponse
// - AgentResponse -> StreamResponse
export type RawChatMessage = HumanMessage | AssistantMessage;
export type RawChat = RawChatMessage[];

export type ChatMessage = HumanMessage | StreamResponse;
export type Chat = ChatMessage[];

export type AssistanceServiceProduct =
	| 'confluence'
	| 'jira-software'
	| 'jira-core'
	| 'jsm'
	| 'jpd'
	| 'atlas'
	| 'jira'
	| 'unknown'
	| 'rovo'
	| 'bitbucket'
	| 'trello'
	| 'elevate'
	| 'recruit'
	| 'mercury'
	| 'admin'
	| ''
	| string;

export type AssistanceServiceConfig = {
	baseUrl?: string | undefined;
	headers?: Record<string, string>;
	siteId?: string;
	/**
	 * Optional value for the `X-Channel-Id` header on chat-related requests.
	 * Defaults to the service-specific channel ID when omitted.
	 */
	channelId?: string;
	product: AssistanceServiceProduct;
	credentials?: RequestInit['credentials'];
	experienceId: string;
	minionAlias?: string;
	labelStack?: LabelStack;
	/**
	 * Optional override for the path used by `validateFiles`. Defaults to
	 * `/rovo/v1/chat/message/file/validate` when not provided.
	 */
	fileValidatePath?: string;
	/**
	 * Optional override for the path used by `uploadFiles`. Defaults to
	 * `/rovo/v1/chat/message/file` when not provided.
	 */
	fileUploadPath?: string;
};

export type ExternalFile = {
	media_file_id: string;
	file_name: string;
	mime_type: string;
};

export type PostAgentPayload = Pick<
	Agent,
	| 'name'
	| 'description'
	| 'named_id'
	| 'system_prompt_template'
	| 'available_plugins'
	| 'visibility'
	| 'external_files'
	| 'user_defined_conversation_starters'
	| 'knowledge_sources'
	| 'action_config'
	| 'creator'
>;

export type ConversationChannelStatus = 'OPEN' | 'ENDED' | 'EXPIRED' | 'ESCALATED';

export type AgentSessionState =
	| 'WORKING'
	| 'INPUT_REQUIRED'
	| 'ARTEFACT_READY'
	| 'AUTH_REQUIRED'
	| 'FAILED'
	| 'REJECTED'
	| 'COMPLETED'
	| 'CANCELLED'
	| 'UNKNOWN';

export type MessageType = 'DEEP_RESEARCH' | 'WEB_SEARCH';

export type ConversationChannelMessageStatus =
	| 'IN_PROGRESS'
	| 'SUCCESSFUL'
	| 'FAILED'
	| 'CANCELLED';

export type LastAgentMessageStatus = 'IN_PROGRESS' | 'SUCCESSFUL' | 'FAILED' | 'CANCELLED';

export type ConversationChannelScope = {
	type: 'image' | 'file' | 'jira' | 'confluence' | 'link';
	name: string;
	link: string;
	icon_url?: string;
	sub_type?: 'space' | 'page' | 'whiteboard';
};

export type ConversationChannelTask = {
	task_id: string;
	conversation_id: string;
	message_id: string | null;
	completion_message_id: string | null;
	current_message_id: string | null;
	agent_id: string | null;
	status: ApiTaskStatus;
	trigger_type: 'EXPLICIT' | 'IMPLICIT';
	title: string;
	plan: { version: number; text: string } | null;
	result_summary: string | null;
	error_message: string | null;
	created_at: string;
	started_at: string | null;
	updated_at: string;
	completed_at: string | null;
	execution_segments: Record<string, string> | null;
	todo_list: Record<string, string> | null;
};

export type ConversationParticipantType = 'AGENT' | 'USER';

export type ConversationAgentParticipantMetadata = {
	agent_id?: string | null;
	agent_named_id?: string | null;
	version_id?: string | null;
	version_number?: number | null;
	version_type?: string | null;
};

export type ConversationUserParticipantMetadata = {
	account_id: string;
};

export type ConversationAgentParticipant = {
	type: 'AGENT';
	participant_id: string;
	agent?: ConversationAgentParticipantMetadata | null;
	user?: never;
};

export type ConversationUserParticipant = {
	type: 'USER';
	participant_id: string;
	user?: ConversationUserParticipantMetadata | null;
	agent?: never;
};

export type ConversationParticipant = ConversationAgentParticipant | ConversationUserParticipant;

export type RestorableRequestedMode = 'MAX' | 'MARATHON' | 'DEEP_RESEARCH';

export type ConversationChannel = {
	readStatus?: boolean | null;
	isPinned?: boolean | null;
	pinnedAt?: string | null;
	sourceMode?: RovoChatSourceMode | null;
	agentSessionState?: AgentSessionState;
	id: string;
	experience_id: string;
	owner: string;
	time_created: string;
	status: ConversationChannelStatus;
	name?: string;
	messages?: Message[];
	last_message?: string;
	last_message_type?: MessageType[];
	last_message_timestamp?: string;
	last_agent_message_status?: ConversationChannelMessageStatus;
	assistant_message_id?: string;
	agent?: AgentMetadata;
	scopes?: ConversationChannelScope[];
	tags?: string[];
	tasks?: ConversationChannelTask[];
	participants?: ConversationParticipant[];
	last_requested_mode?: RestorableRequestedMode | null;
};

export type ConversationChannelsRequest = {
	limit?: number;
	cursor?: string;
};

export type ConversationChannelsResponse = {
	items: ConversationChannel[];
	hasNext: boolean;
	cursor: string | null;
};

export type PinnedConversationSummaryResponse = {
	/** Number of pinned conversations for the caller, capped at `maxPinnedConversations`. */
	pinnedCount: number;
	/** Configured maximum number of pinned conversations. */
	maxPinnedConversations: number;
	// Note: the backend also returns `has_reached_limit`, but the client intentionally does not model
	// it — the limit gate is recomputed from `pinnedCount` plus in-flight optimistic changes, so the
	// precomputed boolean would be stale after an optimistic pin/unpin.
};

export type AgentMetadata = {
	agent_id: string;
	agent_named_id: string;
	agent_name: string;
	identity_account_id?: string;
	creator_type: string;
	forge_url?: string;
};

export interface MessagePayloadAction {
	key: string;
	invocation_id: string;
	planned_data: Record<string, unknown>;
	status: 'PLANNED' | 'STARTED' | 'FINISHED' | 'CANCELLED' | 'ERRORED';
	action_id?: string;
	metadata?: Record<string, unknown>;
	tool_source?: string;
	confirmation_message?: string;
	human_message_id?: string;
	display_name: string;
}

export interface Dialogues {
	human_message: {
		content: string;
	};
	agent_message: {
		content: string;
		/** MIME type of the agent message content.
		 * Use `'text/adf'` when content is ADF JSON.
		 * Sent to the backend API so it stores the content type correctly. */
		mimeType?: 'text/markdown' | 'text/adf';
		/** Actions to be included in the agent message.
		 * Allows creating conversations with pre-populated action data (e.g., template flow). */
		actions?: MessagePayloadAction[];
	};
}

export type SendMessageDashboardContext =
	| {
			content: string;
	  }
	| undefined;

export type SendMessageDashboardEditorContext =
	| {
			content: string;
			dashboardAri?: string;
			isEditing?: boolean;
	  }
	| undefined;

export type SendMessageEditorContext =
	| {
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			document: {
				type: 'text/markdown' | 'text/adf';
				content: string;
			};
			selection: {
				type: 'text/markdown' | 'text/plain';
				content: string;
			};
	  }
	| undefined;

export type SendMessageWhiteboardContext =
	| {
			type: 'image/svg+xml' | 'text/plain';
			content: string;
			contentId?: string;
	  }
	| undefined;

export type SendMessageSlidesContext =
	| {
			xml: string;
			contentId: string;
			title: string;
			url: string;
			selectedSlideIndex: number;
			selectedElementIds: string[];
			isViewMode?: boolean;
	  }
	| undefined;

export type SendMessageDatabaseContext =
	| {
			contentId: string;
			csv: string;
			title: string;
			url: string;
			selectedElementIds?: string[];
			isViewMode?: boolean;
	  }
	| undefined;

export type SendMessageChatContext =
	| {
			[contextKey: string]: unknown;
	  }
	| undefined;

export type SendMessageBrowserContext = {
	context:
		| {
				browserUrl: string;
				htmlBody?: string;
				canvasText?: string;
				selection?: {
					content: string;
				};
		  }
		| undefined;
};

export type AlphaExtendedThinkingMode = 'MARATHON' | 'LONG_HORIZON';

export type RovoChatSourceMode = 'GENERAL' | 'STRICT';

export type SendMessageSearchArtifact = {
	/** The Atlassian Resource Identifier (ARI) of the object. */
	ari?: string;
	/** The URL of the object. */
	url?: string;
};

export type SendMessageWorkflowContext = {
	currentWorkflowDocument?: Record<string, unknown>;
};

export type PermissionId =
	| 'AGENT_CREATE'
	| 'AGENT_READ'
	| 'AGENT_UPDATE'
	| 'AGENT_DEACTIVATE'
	| 'AGENT_DELETE';

export type GetAgentPermissionsPayload = {
	permission_ids: PermissionId[];
};

export type AgentPermissionResponse = {
	permissions: Partial<
		Record<
			PermissionId,
			{
				permitted: boolean;
			}
		>
	>;
};

export type FileUploadResponseItem = {
	id: string;
	conversation_channel_id?: string;
	blob_id?: string;
	name: string;
	size?: number;
	type?: string;
	created_at?: string;
	owner?: string;
};

export type FileMetadataResponseItem = {
	id: string;
	conversation_channel_id: string;
	blob_id: string;
	name?: string;
	size: number;
	type?: string;
	created_at: string;
	owner: string;
};

export type FileUploadResponse = {
	data: FileUploadResponseItem[];
};

export type FileValidationMetadata = {
	originalFilename: string;
	contentType: string;
	size: number;
};

export type FileValidationPayload = {
	fileMetadata: FileValidationMetadata[];
};

export type FileValidationResponse = {
	validationResults: {
		fileMetadata: FileValidationMetadata;
		isSupported: boolean;
		errorCode?:
			| 'INVALID_CONTENT_TYPE'
			| 'FILE_TOO_LARGE'
			| 'FILE_NAME_MISSING'
			| 'UNKNOWN_ERROR'
			| string;
	}[];
};

export type RovoLabsTool = {
	name: string;
	displayName: string;
	description: string;
	/**
	 * This is the JSON schema for the tool's configuration.
	 */
	configurationInJsonSchema?: unknown;
	icon?: string;
	provider: RovoLabsToolProvider;
	integrationKey?: string;
};

export type RovoLabsToolProvider = {
	id: string;
	displayName: string;
};

export type RovoLabsToolPayload = {
	name: string;
	configuration: unknown;
	provider: RovoLabsToolProvider;
};

export type RovoLabsChatStreamPayload = {
	latest_user_message: string | DocNode;
	conversation_id: string;
	workflow_configuration: {
		routerConfig: {
			maxLoop: number;
			selectedTools: RovoLabsToolPayload[];
		};
		toolInvocationConfig: {
			toolInvocationTimeoutMs: number;
		};
		selectedLanguageModelId?: string;
		promptConfig?: {
			systemPromptTemplate?: string;
			includeCurrentUserContext: boolean;
		};
	};
	additional_context?: {
		file_attachment_ids: string[];
	};
	confirmed_tools?: RovoLabsConfirmedTools;
};

export type RovoLabsLanguageModel = {
	modelId: string;
	modelProvider: string;
};

export type RovoLabsDefaultPrompt = {
	defaultSystemPromptTemplate: string;
};

export type RovoLabsConfirmedTool = {
	key: string;
	tool_id?: string;
	tool_source?: string;
	invocation_id: string;
	data: {
		human_message_id: string;
		arguments: unknown;
	};
	resolution?: {
		type?: 'confirmed' | 'cancelled';
		reason?: string | DocNode;
		mimeType?: string;
	};
};

export type RovoLabsConfirmedTools = {
	message_id: string;
	tools: RovoLabsConfirmedTool[];
};

export type StreamToolSelection = {
	type: 'TOOL_SELECTION';
	message: {
		invocationId: string;
		displayName: string;
		toolInput: unknown;
	};
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamToolInvocation = {
	type: 'TOOL_INVOCATION';
	message: {
		invocationId: string;
		displayName: string;
		toolOutput: unknown;
	};
	millisOffset?: number;
};

/**
 * Interface for the Assistance Service.
 */
/**
 * Version info sent to the BE when sending messages.
 * Matches the BE's AgentVersionInput shape.
 */
export type AgentVersionInput = {
	versionId?: string;
	versionType?: string;
};

/**
 * Request-context fields shared by `sendMessageStream` and `sendMessageConfirmedTools`.
 * Both methods POST to the same message-stream endpoint and serialize an identical
 * `body.context`, so the context-shaped payload fields are defined once here to keep the
 * two signatures in sync. Method-specific fields (e.g. `fileIds`, `appFilters` on
 * `sendMessageStream`) stay inline on their respective payloads.
 */
export interface SendMessageContextParams {
	jiraWorkflowContext?: SendMessageWorkflowContext;
	editorContext?: SendMessageEditorContext;
	whiteboardContext?: SendMessageWhiteboardContext;
	slidesContext?: SendMessageSlidesContext;
	databaseContext?: SendMessageDatabaseContext;
	browserContext?: SendMessageBrowserContext;
	additionalContext?: Record<string, unknown>;
	aiFeatureContext?: SendMessageChatContext;
	dashboardContext?: SendMessageDashboardContext;
	dashboardEditorContext?: SendMessageDashboardEditorContext;
	/**
	 * This when enabled will ensure that the browser_url is always used by the Rovo as context
	 */
	useCurrentBrowsingContext?: boolean;
	/**
	 * Sanitizer for browser context URLs.
	 * Used to check if a URL should be blocked from being used as browser context.
	 * If a URL is blocklisted, an empty URL and empty context will be sent instead.
	 */
	browserContextSanitizer?: {
		isBlocklisted: (url: string) => boolean;
	};
	/**
	 * Optional artifact representing the object the user is currently interacting with or
	 * acting on (e.g. 3P forge artifacts that are not resolvable without an ARI).
	 * Sent as `search_artifact` in `body.context`.
	 */
	searchArtifact?: SendMessageSearchArtifact;
}

/**
 * Visibility mode for a shared conversation snapshot.
 *
 * - `ORG`: anyone in the user's organization with the share link can view.
 * - `SPECIFIC_USERS`: only the listed user ARIs can view.
 *
 */
export type ConversationShareType = 'ORG' | 'SPECIFIC_USERS';

/**
 * Request body for `PATCH /api/v2/share`.
 *
 * Backend treats `(conversationId, messageId)` as an idempotency key: calling
 * with the same pair returns the previously-issued `shareToken` instead of
 * minting a new one. To produce a new snapshot, advance the conversation and
 * call again with the new latest assistant `messageId`.
 *
 * Validation rules enforced by the backend:
 * - `shareType === 'SPECIFIC_USERS'` requires a non-empty `addUserAris`.
 * - `shareType === 'ORG'` must omit `addUserAris` entirely.
 */
export type UpsertConversationShareRequest = {
	/** Conversation being shared. */
	conversationId: string;
	/**
	 * Latest assistant message id to include in the snapshot. The snapshot
	 * contains every message up to and including this one; later messages
	 * are excluded.
	 */
	messageId: string;
	shareType: ConversationShareType;
	/**
	 * ARIs of the users granted access. Required when
	 * `shareType === 'SPECIFIC_USERS'`; must be omitted otherwise.
	 */
	addUserAris?: string[];
};

/**
 * Request body for the details mode of `PATCH /api/v2/share`.
 *
 * Omitting `shareType` asks the backend to ensure a share snapshot exists
 * for `(conversationId, messageId)` and return its current access model
 * without changing permissions.
 */
export type EnsureConversationShareRequest = {
	conversationId: string;
	messageId: string;
};

/**
 * Exact replacement payload for `PUT /api/v2/share`.
 *
 * `ORG` shares must omit `allowedUserAris`; `SPECIFIC_USERS` accepts the full
 * intended user list, including an empty list for creator-only sharing.
 */
export type ReplaceConversationShareRequest = {
	conversationId: string;
	messageId: string;
	shareType: ConversationShareType;
	allowedUserAris?: string[];
};

export type GetConversationSharePreviewRequest = {
	conversationId: string;
	messageId: string;
};

export type GetConversationShareMetadataRequest = {
	conversationId: string;
	messageId: string;
};

/**
 * Response body returned by `PATCH /api/v2/share`. The backend returns 201
 * when a new share is created and 200 when the existing share for
 * `(conversationId, messageId)` is returned or updated.
 */
export type ConversationShareResponse = {
	/** Opaque token used to construct the shareable URL. */
	shareToken: string;
	shareType: ConversationShareType;
	/** ISO-8601 timestamp of when the share was first created. */
	createdAt: string;
	/**
	 * ARIs allowed to view the share. `null` when `shareType === 'ORG'`
	 * (anyone in the org can view).
	 */
	allowedUserAris: string[] | null;
	/** ARI of the user who created the share. Present on newer management responses. */
	creatorAri?: string;
	/** Conversation id keyed by the management APIs. Present on newer management responses. */
	conversationId?: string;
	/** Message id keyed by the management APIs. Present on newer management responses. */
	messageId?: string;
};

/**
 * Response body returned by `GET /api/v2/share` with conversation/message
 * query params. Contains the current share access model without creating or
 * replacing the share.
 */
export type GetConversationShareMetadataResponse = ConversationShareResponse;

/** Author of a single message within a shared conversation snapshot. */
export type ConversationShareSnapshotAuthor = {
	id: string;
	actorType: string;
	ari: string | null;
	name: string | null;
};

/**
 * Message content within a shared conversation snapshot. The backend may send
 * a plain string (markdown), an object wrapping `text`, or a structured ADF
 * document, so consumers must narrow before rendering.
 */
export type ConversationShareSnapshotContent = string | { text?: string } | Record<string, unknown>;

/** A citation surfaced by an assistant message in a shared snapshot. */
export type ConversationShareSnapshotSource = {
	ari: string;
	title: string;
	type: string;
	url: string;
	lastModified: string;
	product: string;
};

export type ConversationShareSnapshotActionStatus =
	| 'PLANNED'
	| 'STARTED'
	| 'FINISHED'
	| 'CANCELLED'
	| 'ERRORED';

/**
 * An action invocation captured in a shared snapshot. Present for parity with
 * the live message shape; the read-only preview does not render these as
 * interactive cards.
 */
export type ConversationShareSnapshotAction = {
	key: string;
	displayName: string;
	invocationId: string;
	status: ConversationShareSnapshotActionStatus | null;
	confirmationMessage: string | null;
	data?: unknown;
	actionData?: unknown;
	action_data?: unknown;
	toolInput?: unknown;
	tool_input?: unknown;
	executedData?: unknown;
	executed_data?: unknown;
};

/** A single finalized message in a shared conversation snapshot. */
export type ConversationShareSnapshotMessage = {
	id: string;
	role: 'HUMAN' | 'ASSISTANT';
	author: ConversationShareSnapshotAuthor;
	content: ConversationShareSnapshotContent;
	contentMimeType: string;
	timeCreated: string;
	sources?: ConversationShareSnapshotSource[];
	actions?: ConversationShareSnapshotAction[];
};

/** Agent metadata captured with a shared conversation snapshot. */
export type ConversationShareSnapshotAgent = {
	id: string;
	namedId: string;
	externalConfigReference: string | null;
	identityAccountId: string | null;
	creatorType: string;
	icon: string | null;
};

/**
 * Response body returned by `GET /api/v2/share/{shareToken}`. Contains the
 * snapshot metadata plus the finalized messages captured when the share was
 * created.
 */
export type GetConversationShareResponse = {
	shareToken: string;
	shareType: ConversationShareType;
	/** ISO-8601 timestamp of when the snapshot was created. */
	createdAt: string;
	/** ARI of the user who created the share. */
	creatorAri: string;
	/** Source conversation name captured when the share snapshot was created. */
	conversationName?: string | null;
	/** Agent metadata used to render the shared conversation's avatar. */
	agent?: ConversationShareSnapshotAgent | null;
	messages: ConversationShareSnapshotMessage[];
};

export type GetConversationSharePreviewResponse = Omit<
	GetConversationShareResponse,
	'shareToken' | 'shareType' | 'createdAt'
>;

/**
 * Response body returned by `POST /api/v2/share/{shareToken}/fork`.
 * Contains the newly-created conversation id seeded from the shared snapshot.
 */
export type ForkConversationShareResponse = {
	conversationId: string;
};

/**
 * A single share owned by the current user, as returned by
 * `GET /api/v2/shares/grouped`.
 *
 * Consumers that need a display label should prefer `shareName`, falling back to
 * the owning conversation (`ConversationSharesGroup.conversationName`) or an
 * identifier when it is absent.
 */
export type OwnedShareResponse = {
	/** Opaque token used to construct the shareable URL. */
	shareToken: string;
	shareType: ConversationShareType;
	/** ISO-8601 timestamp of when the share was created. */
	createdAt: string;
	/** Conversation the share belongs to. */
	conversationId: string;
	/** Latest assistant message id captured in the share snapshot. */
	messageId: string;
	/**
	 * Optional human-readable name for the share. Prefer this for the display
	 * label when present.
	 */
	shareName?: string | null;
};

/**
 * A conversation and all of the shares the current user owns within it.
 * `conversationName` is enriched server-side and is `null` when the source
 * conversation name is unavailable.
 */
export type ConversationSharesGroup = {
	conversationId: string;
	conversationName: string | null;
	shares: OwnedShareResponse[];
};

/**
 * Response for `GET /api/v2/shares/grouped`: the current user's owned shares,
 * grouped by conversation. Groups whose source conversation no longer exists
 * are excluded server-side.
 *
 * Returns `404` (surfaced as an error by the client) when the
 * `ROVO_CONVERSATION_SHARING` flag is off or there is no org context.
 */
export type GroupedSharesResponse = {
	conversations: ConversationSharesGroup[];
	/** Whether more groups are available beyond the current page. */
	hasMore: boolean;
};

export interface AssistanceService {
	sendMessageStream: (
		payload: {
			conversationId: string;
			message: string | DocNode;
			mimeType?: string;
			/**
			 * This can be agentId or agentNamedId, and BE will resolve it correctly
			 * https://atlassian.slack.com/archives/C0677M19GU9/p1725952045906819?thread_ts=1725929736.588339&cid=C0677M19GU9
			 *
			 * As of RAGE-60 the default agent is no longer required to be sent to the backend. So agentNamedId can be undefined.
			 */
			agentNamedId?: string;
			agentId?: string;
			controller?: AbortController;
			storeMessage?: boolean;
			/** Whether to keep the source indexes in the message e.g [^1^], [^2^] */
			citationsEnabled?: boolean;
			minionAliasConfig?: Record<string, unknown>;
			fileIds?: string[];
			deepResearchEnabled?: boolean;
			thinkDeeperEnabled?: boolean;
			alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
			fastModeEnabled?: boolean;
			planModeEnabled?: boolean;
			webSearchEnabled?: boolean;
			sourceMode?: RovoChatSourceMode;
			/**
			 * appFilters being undefined means all org knowledge is applied.
			 * appFilters being empty means no org knowledge is applied.
			 * appFilters being non-empty means some org knowledge is applied.
			 */
			appFilters?: string[];
			/**
			 * Non-current cloud IDs selected from the Rovo sources site filter.
			 * When undefined or empty the backend uses the current site.
			 */
			selectedCloudIds?: string[];
			optInToDeepResearchV2?: boolean;
			optInThinkDeeperMessages?: boolean;
			optInAnsNow?: boolean;
			optInNewToolTraces?: boolean;
			optInNewTeamworkTraceIcons?: boolean;
			optInSSEStream?: boolean;
			agentVersion?: AgentVersionInput;
			spaceId?: string;
			/**
			 * A list of capability strings the client is opting in to.
			 * Sent as the `X-Rovo-Chat-Capabilities` header (JSON array).
			 * When undefined or empty the header is omitted for backward compatibility.
			 */
			capabilities?: string[];
			/**
			 * When provided, controls whether BE should extract conversation scopes from current message context.
			 */
			shouldExtractConversationScopes?: boolean;
			parentMessageId?: string;
			/** Skill names explicitly selected by the client for this turn. */
			requestedSkills?: string[];
			/**
			 * A per-request Writing Styles override. Omit this to preserve the user's
			 * saved response style and the existing Chat behavior.
			 */
			writingStyleContext?: WritingStyleContext;
		} & SendMessageContextParams,
	) => Promise<FetchStreamReturn<StreamMessage, boolean>>;
	reconnectToMessageStream: (payload: {
		conversationId: string;
		messageId: string;
		lastEventId?: string;
		controller?: AbortController;
	}) => Promise<FetchStreamReturn<StreamMessage, true>>;
	/**
	 * Cancel an async streaming task for a message.
	 * Calls the backend API to stop processing the request.
	 */
	cancelMessageStream: (payload: { conversationId: string; messageId: string }) => Promise<void>;
	sendMessageConfirmedTools: (
		payload: {
			conversationId: string;
			agentId?: string;
			controller?: AbortController;
			/**
			 * This is used as an alternative way to execute action/tools
			 * replacing the need to go through `/action` endpoint
			 * https://hello.atlassian.net/wiki/spaces/agents/pages/5974706697/Short-term+Enable+resumption+for+external+MCP
			 */
			confirmed_tools: RovoLabsConfirmedTools;
			optInSSEStream?: boolean;
			storeMessage?: boolean;
			webSearchEnabled?: boolean;
			deepResearchEnabled?: boolean;
			thinkDeeperEnabled?: boolean;
			alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
			fastModeEnabled?: boolean;
			planModeEnabled?: boolean;
			agentVersion?: AgentVersionInput;
			optInToDeepResearchV2?: boolean;
			optInThinkDeeperMessages?: boolean;
			optInAnsNow?: boolean;
			optInNewToolTraces?: boolean;
			optInNewTeamworkTraceIcons?: boolean;
			/**
			 * A list of capability strings the client is opting in to.
			 * Sent as the `X-Rovo-Chat-Capabilities` header (JSON array).
			 * When undefined or empty the header is omitted for backward compatibility.
			 */
			capabilities?: string[];
		} & SendMessageContextParams,
	) => Promise<FetchStreamReturn<StreamMessage, boolean>>;
	nlCreateAgentStream: (
		payload: {
			controller?: AbortController;
		} & NLCreateAgentPayload,
	) => Promise<FetchStreamReturn<NLCreateAgentStreamMessage>>;
	getConfig: () => AssistanceServiceConfig;
	getChat: (conversationId: string, capabilities?: string[]) => Promise<Chat>;
	deleteChat: (conversationId: string) => Promise<void>;
	getAgents: () => Promise<Agent[]>;
	getAgentDetails: (id: string) => Promise<AgentDetails>;
	getAgentDetailsByIdentityAccountId: (identityAccountId: string) => Promise<AgentDetails>;
	createAgent: (payload: PostAgentPayload) => Promise<Agent>;
	updateAgent: (id: string, payload: PostAgentPayload) => Promise<Agent>;
	deleteAgent: (id: string) => Promise<void>;
	favouriteAgent: (id: string) => Promise<void>;
	unfavouriteAgent: (id: string) => Promise<void>;
	getConversationChannels: (
		options?: ConversationChannelsRequest,
	) => Promise<ConversationChannelsResponse>;
	/**
	 * Authoritative, cross-container pinned-conversation summary. Optional so hosts that do not
	 * implement it (and callers before the backend endpoint is deployed) fall back to a local count.
	 * Resolves to `undefined` when the endpoint is unavailable (e.g. `rovo_chat_pinned_be` off).
	 */
	getPinnedConversationSummary?: () => Promise<PinnedConversationSummaryResponse | undefined>;
	createConversationChannel: (options?: {
		name?: string | null;
		dialogues?: Dialogues[];
		tags?: string[];
		agentId?: string;
	}) => Promise<ConversationChannel>;
	updateConversationChannel: (
		id: string,
		payload: Partial<ConversationChannel>,
	) => Promise<ConversationChannel>;
	resolveConversationAction: (
		conversationId: string,
		payload: MessageActionPayload,
	) => Promise<MessageActionResponse>;

	bulkResolveConversationUIActions: (
		conversationId: string,
		messageId: string,
		payload: BulkMessageActionPayload,
	) => Promise<BulkMessageActionResponse>;

	// REST conversation starters, newer API
	generateAgentStarters: (payload: {
		agent_name?: string;
		description?: string;
		system_prompt?: string;
	}) => Promise<ConversationStarterResponse>;

	// REST conversation starters, newer API
	generateContextualStarters: (payload: {
		browser_url: string;
	}) => Promise<ConversationStarterResponse>;

	// REST dynamic conversation starters (new endpoint. /convo-starters)
	getDynamicConvoStartersNew: (
		payload: DynamicConversationStarterRequestNew,
	) => Promise<DynamicConversationStarterResponseNew>;

	getAgentActions: () => Promise<AgentAction[]>;
	getAgentKnowledgeConfiguration: (
		agentCreatorType?: AgentCreatorType,
	) => Promise<AgentKnowledgeConfigurationResponse>;
	getAgentPermissions: (
		agentId: string,
		payload: GetAgentPermissionsPayload,
	) => Promise<AgentPermissionResponse>;
	getRecommendedAgents: (payload: { limit: number; pageUrl?: string }) => Promise<{
		agents: Agent[];
		metadata: { recommendationKey: string };
	}>;
	getConversationDebugLogs: (conversationId: string) => Promise<Chat>;
	uploadFiles: (payload: { files: File[] }) => Promise<FileUploadResponse>;
	validateFiles: (payload: FileValidationPayload) => Promise<FileValidationResponse>;
	rovoLabsSendMessage: (
		payload: RovoLabsChatStreamPayload,
	) => Promise<FetchStreamReturn<StreamMessage | StreamToolSelection | StreamToolInvocation>>;
	rovoLabsGetConversations: () => Promise<{ items: ConversationChannel[] }>;
	rovoLabsGetTools: () => Promise<{ tools: RovoLabsTool[] }>;
	rovoLabsGetLanguageModels: () => Promise<{ languageModels: RovoLabsLanguageModel[] }>;
	rovoLabsGetPrompts: () => Promise<RovoLabsDefaultPrompt>;
	getWebSearchConfiguration: () => Promise<WebSearchConfigurationResponse>;
	getPrompts: () => Promise<GetAllPromptsResponse>;
	getPrompt: (promptId: string) => Promise<GetPromptResponse>;
	bulkGetPrompts: (payload: BulkGetPromptsRequest) => Promise<BulkGetPromptsResponse>;
	createPrompt: (payload: CreatePromptRequest) => Promise<GetPromptResponse>;
	updatePrompt: (promptId: string, payload: UpdatePromptRequest) => Promise<GetPromptResponse>;
	/**
	 * `shareId` is the same value as `GetPromptResponse.id`, which is the only prompt identifier the
	 * client ever holds. The prompt-library write surface names it `shareId` throughout
	 * (`AddPromptsToLibraryRequest`, `FavoritePromptItem`, `BulkUpdatePrivacyItem`,
	 * `BulkGetPromptsRequest`) because a prompt in your library is a share of a prompt, while the read
	 * surface calls the same field `id`. It is not a second kind of identifier, so there is nothing to
	 * mix up here: `deletePrompt(prompt.id)` is correct, and matches `getPrompt`/`updatePrompt`, which
	 * name the identical value `promptId`.
	 */
	deletePrompt: (shareId: string) => Promise<void>;
	addPromptsToLibrary: (payload: AddPromptsToLibraryRequest) => Promise<void>;
	favoritePrompts: (payload: FavoritePromptsRequest) => Promise<void>;
	bulkUpdatePrivacy: (payload: BulkUpdatePrivacyRequest) => Promise<BulkUpdatePrivacyResponse>;
	upsertConversationShare: (
		payload: UpsertConversationShareRequest,
	) => Promise<ConversationShareResponse>;
	ensureConversationShare: (
		payload: EnsureConversationShareRequest,
	) => Promise<ConversationShareResponse>;
	replaceConversationShare: (
		payload: ReplaceConversationShareRequest,
	) => Promise<ConversationShareResponse>;
	getConversationShare: (shareToken: string) => Promise<GetConversationShareResponse>;
	/** Fetch the current user's owned shares, grouped by conversation. */
	getGroupedConversationShares: () => Promise<GroupedSharesResponse>;
	getConversationShareMetadata: (
		payload: GetConversationShareMetadataRequest,
	) => Promise<GetConversationShareMetadataResponse>;
	getConversationSharePreview: (
		payload: GetConversationSharePreviewRequest,
	) => Promise<GetConversationSharePreviewResponse>;
	/**
	 * Delete a single share snapshot identified by its opaque `shareToken`. Only
	 * the creator of the snapshot may delete it; the server responds with `204`
	 * on success and `404` when the snapshot is missing or not owned by the
	 * caller.
	 */
	deleteConversationShare: (shareToken: string) => Promise<void>;
	/**
	 * Delete every share snapshot belonging to a conversation. Only the
	 * conversation owner may perform this; the server responds with `204` on
	 * success.
	 */
	deleteAllSharesForConversation: (conversationId: string) => Promise<void>;
	forkSharedConversation: (shareToken: string) => Promise<ForkConversationShareResponse>;
	getGeneratedContent: (payload?: {
		actions?: string[];
		statuses?: string[];
		limit?: number;
	}) => Promise<ConversationContent[]>;
	getRovoUserSettings: () => Promise<RovoUserSettings>;
	updateRovoUserSettings: (settings: Partial<RovoUserSettings>) => Promise<RovoUserSettings>;
	getRovoMemories: () => Promise<RovoMemoriesResponse>;
	getRovoMemoryBlurb: () => Promise<RovoMemoryBlurbResponse>;
	getRovoMemoryEdits: () => Promise<RovoMemoryEditsResponse>;
	getRovoMemoryEditsCount: () => Promise<RovoMemoryEditsCountResponse>;
	deleteRovoMemoryEdit: (editId: string) => Promise<void>;
	answerNow: (conversationId: string, messageId: string) => Promise<void>;
	deleteRovoMemory: (memoryId: string) => Promise<void>;
	createMemoryEdit: (payload: MemoryEditRequest) => Promise<MemoryEditPromptResponse>;
	createMemory: (payload: CreateMemoryRequest) => Promise<void>;
	fetchConversationMetadata: (conversationId: string) => Promise<ConversationChannel>;
	fetchPersonalizedPromptSuggestions: (config: {
		offset?: number;
		limit?: number;
		/** Wire content-type value ('live doc' | 'whiteboard' | 'database' | 'slide') to scope tiles to a single type. */
		contentType?: ContentType;
		/** Originating surface (e.g. 'blank_object'); selects the backend generation strategy. */
		entryPoint?: string;
	}) => Promise<SuggestedPromptTile[]>;
	fetchPersonalizedPromptSuggestionsStream: (config: {
		offset?: number;
		limit?: number;
		/** Wire content-type value ('live doc' | 'whiteboard' | 'database' | 'slide') to scope tiles to a single type. */
		contentType?: ContentType;
		/** Originating surface (e.g. 'blank_object'); selects the backend generation strategy. */
		entryPoint?: string;
		/**
		 * When true, asks the backend to generate the third-party connector prompt
		 * tile. The frontend owns all experiment logic (including where the tile is
		 * placed) and passes this as a plain boolean; the backend uses it only to
		 * gate 3P tile generation.
		 */
		enable3pPrompt?: boolean;
	}) => Promise<FetchStreamReturn<SuggestedPromptTileStreamMessage>>;
	getTasks: (params?: { limit?: number; cursor?: string }) => Promise<ApiTaskListResponse>;
	getTasksStream: (
		controller?: AbortController,
	) => Promise<FetchStreamReturn<ApiTaskSSEUpdate, true>>;
	cancelTask: (taskId: string) => Promise<void>;
	deleteTask: (taskId: string) => Promise<void>;
	renameTask: (taskId: string, title: string) => Promise<ApiTask>;
}

export type ApiTaskStatus =
	| 'DRAFT'
	| 'QUEUED'
	| 'RUNNING'
	| 'AWAITING_DISAMBIGUATION'
	| 'AWAITING_PLAN_CONFIRMATION'
	| 'AWAITING_ACTION_CONFIRMATION'
	| 'COMPLETED'
	| 'FAILED'
	| 'CANCELLED';

export type ApiTask = {
	task_id: string;
	conversation_id: string;
	message_id: string | null;
	completion_message_id?: string | null;
	current_message_id?: string | null;
	agent_id: string | null;
	status: ApiTaskStatus;
	trigger_type: 'EXPLICIT' | 'IMPLICIT';
	title: string;
	plan: {
		version: number;
		text: string;
	} | null;
	result_summary: string | null;
	error_message: string | null;
	created_at: string;
	started_at: string | null;
	updated_at: string;
	completed_at: string | null;
	execution_segments?: Record<string, string> | null;
};

export type ApiTaskListResponse = {
	tasks: ApiTask[];
	total_count: number | null;
	cursor: string | null;
};

// ************************************************************************************************
// Tasks SSE Types
// ************************************************************************************************

export type ApiTaskSSEEventType =
	| 'connected'
	| 'task_status'
	| 'task_snapshot'
	| 'task_todo_update';

export type ApiTaskCurrentStepStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export type ApiTaskCurrentStep = {
	id: string;
	content: string;
	status: ApiTaskCurrentStepStatus;
	priority?: 'LOW' | 'MEDIUM' | 'HIGH';
};

/**
 * Represents the `task_snapshot` event — emitted on connect with the current state
 * of all active tasks for the user.
 *
 * Example payload:
 * {
 *   "tasks": [
 *     { "task_id": "ee803e7a-...", "title": "...", "status": "DRAFT", ... }
 *   ]
 * }
 */
export type ApiTaskSnapshotSSEUpdate = {
	tasks: ApiTaskSSEUpdate[];
};

/**
 * Represents a single Task Status event emitted by the Tasks SSE stream
 * (GET /rovo/v1/tasks/stream).
 *
 * The `event_type` field is carried as a separate SSE envelope field and is
 * not part of the event data payload itself.
 *
 * Example payload:
 * {
 *   "task_id": "ari:cloud:convo-ai::task/abc-123",
 *   "title": "Create QBR narrative from Q1 data",
 *   "status": "RUNNING",
 *   "current_step": { "id": "step-2", "content": "Analyze trends", "status": "IN_PROGRESS", "priority": "HIGH" },
 *   "result_summary": null,
 *   "error_message": null,
 *   "message_id": "msg-789",
 *   "completion_message_id": null,
 *   "created_at": "2026-04-02T10:30:00Z",
 *   "started_at": "2026-04-02T10:30:05Z",
 *   "updated_at": "2026-04-02T10:35:12Z"
 * }
 */
export type ApiTaskSSEUpdate = {
	task_id: string;
	title: string;
	status: ApiTaskStatus;
	current_step: ApiTaskCurrentStep | null;
	result_summary: string | null;
	error_message: string | null;
	conversation_id?: string | null;
	message_id?: string | null;
	completion_message_id?: string | null;
	current_message_id?: string | null;
	created_at?: string | null;
	started_at?: string | null;
	updated_at: string;
	execution_segments?: Record<string, string> | null;
	todo_list?: Record<string, string> | null;
};

// ************************************************************************************************
// Type Guards
// ************************************************************************************************

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isChatMessage = (message: any): message is ChatMessage => {
	return isHumanMessage(message) || isAssistantMessage(message) || isStreamResponse(message);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isHumanMessage = (message: any): message is HumanMessage =>
	message instanceof Object && message.role === 'HUMAN';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAssistantMessage = (message: any): message is AssistantMessage =>
	message instanceof Object && message.role === 'ASSISTANT';

/**
 * True when the assistant message was carried over from a shared conversation snapshot rather than
 * produced by the live conversation it now sits in.
 */
export const isSharedSnapshotMessage = (message: AssistantMessage): boolean =>
	Boolean(message.message_metadata?.sharedFromMessageId);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAgentResponse = (message: any): message is AgentResponse =>
	message instanceof Object && isAssistantMessage(message.message);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamMessageWithMessage = (message: any): message is StreamMessageWithMessage =>
	message instanceof Object && message.message instanceof Object;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isInProgressStreamResponse = (message: any): message is StreamResponse =>
	isStreamResponse(message) && message.message?.message?.async_status === 'IN_PROGRESS';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResponse = (message: any): message is StreamResponse =>
	message instanceof Object && message.type === 'FINAL_RESPONSE';

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const isOptionalNullableString = (value: unknown): value is string | null | undefined =>
	value === undefined || value === null || typeof value === 'string';

export const isStreamInteractionConclusion = (
	message: unknown,
): message is StreamInteractionConclusion => {
	if (
		!isRecord(message) ||
		message.type !== 'INTERACTION_CONCLUSION' ||
		!isRecord(message.message) ||
		(message.message.outcome !== 'SUCCEEDED' && message.message.outcome !== 'FAILED')
	) {
		return false;
	}

	if (message.millisOffset !== undefined && typeof message.millisOffset !== 'number') {
		return false;
	}

	if (
		message.metadata != null &&
		(!isRecord(message.metadata) ||
			!isOptionalNullableString(message.metadata.request_id) ||
			!isOptionalNullableString(message.metadata.message_id))
	) {
		return false;
	}

	return true;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamError = (message: any): message is StreamError =>
	message instanceof Object && message.type === 'ERROR';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamAUPError = (message: any): message is StreamError =>
	isStreamError(message) && message.message?.message_template === ACCEPTABLE_USE_VIOLATIONS_ERROR;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamAnswerPart = (message: any): message is StreamAnswerPart =>
	message instanceof Object && message.type === 'ANSWER_PART';

const isRawEntityAnnotation = (value: unknown): value is RawEntityAnnotation =>
	isRecord(value) &&
	(value.type === 'person' ||
		value.type === 'Person' ||
		value.type === 'inferred_project' ||
		value.type === 'InferredProject') &&
	typeof value.mention === 'string' &&
	typeof value.start === 'number' &&
	Number.isFinite(value.start) &&
	typeof value.end === 'number' &&
	Number.isFinite(value.end) &&
	typeof value.ari === 'string' &&
	typeof value.canonical_name === 'string' &&
	typeof value.confidence_score === 'number' &&
	Number.isFinite(value.confidence_score);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamEntityAnnotations = (message: any): message is StreamEntityAnnotations =>
	isRecord(message) &&
	message.type === 'ENTITY_ANNOTATIONS' &&
	isRecord(message.message) &&
	typeof message.message.sentence === 'string' &&
	Array.isArray(message.message.entities) &&
	message.message.entities.every(isRawEntityAnnotation);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamReasoningPart = (message: any): message is StreamReasoningPart =>
	message instanceof Object && message.type === 'REASONING_PART';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamActionPart = (message: any): message is StreamActionPart =>
	message instanceof Object && message.type === 'ACTION_PART';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamActionClassification = (message: any): message is StreamActionClassification =>
	message instanceof Object && message.type === 'ACTION_CLASSIFICATION';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamActionPrepare = (message: any): message is StreamActionPrepare =>
	message instanceof Object && message.type === 'ACTION_PREPARE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamActionComplete = (message: any): message is StreamActionComplete =>
	message instanceof Object && message.type === 'ACTION_COMPLETE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStart = (message: any): message is StreamResearchStart =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_START';

export const isStreamResearchBeginningTrace = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is StreamResearchStepContent =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStepContent = (message: any): message is StreamResearchStepContent =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStepTitle = (message: any): message is StreamResearchStepTitle =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_TITLE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchPlan = (message: any): message is StreamResearchPlan =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_PLAN';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamTodoSnapshot = (message: any): message is StreamTodoSnapshot =>
	message instanceof Object && message.type === 'TODO_SNAPSHOT';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamTaskStatus = (message: any): message is StreamTaskStatus =>
	message instanceof Object && message.type === 'TASK_STATUS';

export const isStreamResearchStepTitleShort = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is StreamResearchStepTitleShort =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_TITLE_SHORT';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamSkillStart = (message: any): message is StreamSkillStart =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'SKILL_START';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamSkillEnd = (message: any): message is StreamSkillEnd =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'SKILL_END';

export const isStreamFastModeResponseAvailable = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is StreamFastModeResponseAvailable =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'FAST_MODE_RESPONSE_AVAILABLE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamFastModeEnabled = (message: any): message is StreamFastModeEnabled =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'FAST_MODE_ENABLED';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamRemoveAnswerNow = (message: any): message is StreamRemoveAnswerNow =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'REMOVE_ANSWER_NOW';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStepSource = (message: any): message is StreamResearchStepSource =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_SOURCE';

export const isStreamResearchStepCompleted = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is StreamResearchStepCompleted =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_COMPLETE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStepIcons = (message: any): message is StreamResearchStepIcons =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_ICONS';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResearchStepDocument = (message: any): message is StreamResearchStepDocument =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_DOCUMENT';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamReconnectSupportPart = (message: any): message is StreamReconnectSupportPart =>
	message instanceof Object && message.type === 'RECONNECT_SUPPORTED';
export const isStreamResearchPayload = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is
	| StreamResearchTrace
	| StreamResearchStepContent
	| StreamResearchStepTitle
	| StreamResearchStepTitleShort
	| StreamResearchStepSource
	| StreamResearchStepCompleted
	| StreamResearchStepIcons
	| StreamResearchStepDocument =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	(message.message.message_template === 'RESEARCH' ||
		message.message.message_template === 'RESEARCH_STEP' ||
		message.message.message_template === 'RESEARCH_STEP_TITLE' ||
		message.message.message_template === 'RESEARCH_STEP_TITLE_SHORT' ||
		message.message.message_template === 'RESEARCH_STEP_SOURCE' ||
		message.message.message_template === 'RESEARCH_STEP_COMPLETE' ||
		message.message.message_template === 'RESEARCH_STEP_ICONS' ||
		message.message.message_template === 'RESEARCH_STEP_DOCUMENT');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamSkill = (message: any): boolean =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	(isStreamSkillStart(message) || isStreamSkillEnd(message));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamMemoryUpdate = (message: any): message is StreamMemoryUpdateTrace =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message?.message_template === 'MEMORY_UPDATE';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamTrace = (message: any): message is StreamTrace => {
	// TRACE and research steps are sharing the same type, but we want to separate them
	if (isStreamResearchPayload(message)) {
		return false;
	}

	if (isStreamResearchPlan(message)) {
		return false;
	}

	if (isStreamSkill(message)) {
		return false;
	}

	if (isStreamFastModeResponseAvailable(message)) {
		return false;
	}

	if (isStreamThinkDeeperTriggered(message)) {
		return false;
	}

	return message instanceof Object && message.type === 'TRACE';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamThinkDeeperTriggered = (message: any): boolean => {
	return (
		message instanceof Object &&
		message.type === 'TRACE' &&
		message.message?.message_template === 'THINK_DEEPER_TRIGGERED'
	);
};

export const isFollowUpMessage = (message: unknown): message is StreamFollowUp =>
	message !== undefined &&
	message !== null &&
	typeof message === 'object' &&
	'type' in message &&
	message.type === 'FOLLOW_UP_QUERIES';

export const isStreamAgenticUi = (
	message: unknown,
): message is ParsedSSEMessage<StreamAgenticUi> => {
	if (!isSSEMessage<unknown>(message) || message.eventType !== 'AGENTIC_UI') {
		return false;
	}

	if (message.data === null || typeof message.data !== 'object') {
		return false;
	}

	const data = message.data as { message?: { content?: unknown } };
	return Array.isArray(data.message?.content);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamHeartbeat = (message: any): message is StreamHeartbeat =>
	message instanceof Object && message.type === 'HEART_BEAT';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isActionExecutedSuccessfully = (message: any): boolean =>
	isAssistantMessage(message) &&
	(message?.message_metadata?.is_action_execution ?? false) &&
	message?.message_metadata?.action_execution_result === 'SUCCESS';

export const isActionResolutionCancelled = (
	payloadContext: MessageActionPayloadContext,
): boolean => {
	return payloadContext?.resolution?.type === 'cancel';
};

export const isChannelNotFoundError = (error: unknown): boolean =>
	isApiError(error) && error.errorAttributes?.errorCode === CONVERSATION_CHANNEL_NOT_FOUND_ERROR;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamActionError = (msg: any): msg is StreamActionError =>
	msg && msg.type === 'ACTION_ERROR' && msg.message && msg.message.status === 'ERRORED';

export const isRateLimitedError = (error: unknown): boolean =>
	isApiError(error) && error.errorAttributes.statusCode === 429;

export const isHipaaError = (error: unknown): boolean =>
	isApiError(error) &&
	!!error.errorAttributes.errorCode &&
	error.errorAttributes.errorCode === ASSISTANCE_SERVICE_ERROR_CODES.HAS_HIPAA_ENABLED;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAUPViolationError = (error: any): boolean =>
	error instanceof Object &&
	error?.detail?.type === 'ERROR' &&
	!!error.errorAttributes.errorCode &&
	error.errorAttributes.errorCode === ACCEPTABLE_USE_VIOLATIONS_ERROR;

export const isConversationChannelDataMessage = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is ConversationChannelDataMessage =>
	message instanceof Object && message.type === 'CONVERSATION_CHANNEL_DATA';

export const isScopeDeterminedMessage = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is ScopeDeterminedMessage =>
	message instanceof Object && message.type === 'SCOPE_DETERMINED';

export const isSpaceSourceRecommendationsMessage = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: any,
): message is SpaceSourceRecommendationsMessage =>
	message instanceof Object &&
	message.type === 'SPACE_SOURCE_RECOMMENDATIONS' &&
	message.message instanceof Object;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamPromptRedaction = (message: any): message is StreamPromptRedaction =>
	message instanceof Object &&
	(message.type === 'PROMPT_REDACTION' || message.type === 'PROMPT_REDACTED');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamPromptBlock = (message: any): message is StreamPromptBlock =>
	message instanceof Object &&
	(message.type === 'PROMPT_BLOCKED' || message.type === 'PROMPT_BLOCK');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResponseRedaction = (message: any): message is StreamResponseRedaction =>
	message instanceof Object && message.type === 'RESPONSE_REDACTION';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamResponseBlock = (message: any): message is StreamResponseBlock =>
	message instanceof Object && message.type === 'RESPONSE_BLOCKED';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isStreamAttachmentBlock = (message: any): message is StreamAttachmentBlock =>
	message instanceof Object && message.type === 'ATTACHMENT_BLOCKED';

export function isConfirmAction(payloadContext: MessageActionPayloadContext): boolean {
	return payloadContext?.resolution?.type === 'confirm';
}

export type Source = {
	ari: string;
	title: string;
	type: string;
	url: string;
	lastModified: string;
	connector_info?: {
		provider_id?: string;
		connector_product?: string;
		connector_type?: string;
		searchRetrievalType?: string;
		sourceProduct?: string;
	} | null;
	connectorInfo?: {
		provider_id?: string;
		connector_product?: string;
		connector_type?: string;
		searchRetrievalType?: string;
		sourceProduct?: string;
	} | null;
	/** @private @deprecated this is no longer being returned by BE, keeping it because some legacy unified-help code is still using it */
	id?: number;
	product?: string;
};

export type Sources = Array<Source>;

/**
 * BE-provided classification for why no content was created in the AIFC staging area.
 * The FE uses these to route the experience between `fail` and `abort` and to choose
 * the variant of the no-content-created screen. Missing or unrecognised values are
 * treated as a fail (error screen).
 */
export type NoCreateClassification =
	| 'BAD_ROVO_REPLY'
	| 'LIED_ABOUT_CREATION'
	| 'BAD_USER_PROMPT'
	| 'GOOD_REPLY_NO_CREATE'
	| 'EXTERNAL_ERROR';

export type Metadata = {
	deep_research?: boolean;
	interaction_mode_requested?: string;
	interaction_mode_executed?: string;
	// Unified Help Only
	bm25Variant?: string;
	confidenceScore?: number;
	isFallbackMessage?: boolean;
	originalQuery?: string;
	semanticSearchLocationVariant?: string;
	semanticSearchVariant?: string;
	action_execution_result?: string;
	is_action_execution?: boolean;
	invocation_id?: string;
	remote_entity_id?: string;
	requestId?: string;
	actionOutput?: {
		issueId?: string;
		url?: string;
		conversationId?: string;
		_originalResponse?: {
			requestUrl?: string;
			conversationAri?: string;
			channelAri?: string;
			availability?: {
				outcome?: string;
				reason?: string;
				criteria?: string;
			};
			issueKey?: string;
		};
	};
	links?: {
		base?: string;
		webui?: string;
	};
	orchestrator_depth_limit_reached?: boolean;
	orchestrator_context_window_usage?: number;
	/**
	 * AIFC: BE-provided classification for why no content was created in the staging area.
	 * Used by the no-content-created screen to show a differentiated message and to route
	 * the experience event between fail/abort. The set of values is intentionally enumerated
	 * here so that adding/removing a classification is a typed, traceable change. Unknown
	 * values received at runtime are collapsed into the safe fail path on the FE.
	 */
	aifc_no_create_classification?: NoCreateClassification;
	/** Studio SC3 automation rule UUID after ScheduleRovoChatTool execution (success message). */
	ruleIdUuid?: string;
	/** Studio SC3 numeric rule id (when provided alongside {@link ruleIdUuid}). */
	ruleId?: number;
	/** Automation rule rRule after ScheduleRovoChatTool execution (success message). */
	rRule?: string;
	key?: string;
	/** Present when this message was shared from a snapshot; contains the original message ID. */
	sharedFromMessageId?: string;
	/** Present in history responses — authority metadata is nested here rather than at the top level of Message. */
	authority_ui_metadata?: AuthorityUiMetadata | null;
};

export type Appendix = {
	type: 'requestForm' | 'helpDesk';
	content: string;
	appendix_sources?: Sources;
};

export type Appendices = Array<Appendix>;

type InternalTool = {
	name: string;
	/** true if this tool is an action */
	is_consequential: boolean;
	children: InternalTool[] | null;
};

/**
 * An array of arrays of minion/skill outputs
 */
export type MinionOutputs =
	| null
	| {
			minion_id: string;
			minion_input: string;
			minion_output: string;
			minion_execution_duration: number;
			minion_extra_info: string | null;
			/** is this an explicit skill/minion invocation */
			directly_invoked: boolean;
			/** skill slash command/slug */
			skill_alias: string | null;
			/** if this is a stratus skill or something native like search */
			skill_type: 'NATIVE' | 'STRATUS';
			/** all of the tools and actions triggered */
			internal_tools: InternalTool[];
	  }[][];

export type AgentAuthor = {
	id: string;
	versionId?: string;
	versionNumber?: number;
	versionType?: string;
};

/**
 * Start and end indices of insertable content substrings in FINAL_RESPONSE
 */
export type InsertableSection = {
	start: number;
	end: number;
};

/**
 * A single source that contributed to an authoritative answer.
 * Part of {@link AuthorityUiMetadata}.
 */
export type AuthoritySource = {
	title: string;
	url: string | null;
	/** Authority score for this source, normalized 0–1. */
	authorityScore: number;
	/** Human-readable explanation of why this source is considered authoritative. */
	reasoning: string;
	/** Source type (e.g. "page", "blog", "space") — used for icon selection. */
	type?: string;
	/** Owner or team responsible for this source. */
	owner?: string;
	/** ISO date string of when the source was last updated. */
	lastUpdatedDate?: string;
};

/**
 * A domain expert who regularly contributes to topics similar to the answer.
 * Part of {@link AuthorityUiMetadata}.
 */
export type AuthorityDomainExpert = {
	name: string;
	/** ARI identifying the expert. */
	identifier: string;
	role: string;
	/** Human-readable explanation of why this person is considered a domain expert. */
	reasoning: string;
};

/**
 * Backend-provided metadata describing the authority of an assistant answer.
 *
 * Surfaces (Rovo Chat, SAIN) should only render the authoritative-answer
 * treatment when this object is present AND `isHighlyAuthoritative` is `true`.
 */
export type AuthorityUiMetadata = {
	/** Coarse authority bucket; only `"high"` triggers the UI treatment. */
	authorityLevel: 'high' | 'medium' | 'low';
	/** Primary gate for rendering the authoritative-answer UI. */
	isHighlyAuthoritative: boolean;
	/**
	 * History responses use `highlyAuthoritative` (no `is` prefix) — BE inconsistency.
	 * Use `isHighlyAuthoritative ?? highlyAuthoritative` when reading this field.
	 */
	highlyAuthoritative?: boolean;
	/** Overall confidence in the authority assessment, normalized 0–1. */
	overallConfidence: number;
	/** Human-readable summary of the overall authority reasoning. */
	overallReasoning: string;
	sources: AuthoritySource[];
	domainExperts: AuthorityDomainExpert[];
};

export type Message = {
	role: string;
	content: string | DocNode;
	id: string;
	time_created: string;
	user_ari: string;
	plugin_invocations: PluginInvocationMessage[];
	sources?: Sources;
	/**
	 * Backend-provided authority assessment for this message. `null`/absent means
	 * no special authoritative-answer treatment should be rendered.
	 */
	authority_ui_metadata?: AuthorityUiMetadata | null;
	message_metadata?: Metadata;
	author?: {
		actor_type: string;
		ari: string | null;
		id: string;
		name: string;
		/**
		 * @deprecated Use `external_config_reference` on the `Agent` object instead.
		 * `named_id` is being phased out. For OOTB avatar lookup, use
		 * `agent.external_config_reference` as `agentNamedId` on `AgentAvatar`.
		 */
		named_id?: string;
		/** Canonical agent identifier for OOTB avatar lookup (replaces named_id). */
		external_config_reference?: string;
		/** Forge/A2A agent identity account id for avatar resolution. */
		identity_account_id?: string;
		/** Creator type used to determine if this is a Forge agent. */
		creator_type?: string;
		/** Forge agent icon URL (used when rovo_agent_support_a2a_avatar is on). */
		icon?: string;
		versionId?: string;
		versionNumber?: number;
		versionType?: string;
	};
	agent_author?: AgentAuthor;
	author_id?: string;
	conversation_channel_id?: string;
	appendices?: Appendices;
	actions?: MessageAction[];
	async_status?: 'IN_PROGRESS' | 'SUCCESSFUL' | 'FAILED' | 'CANCELLED';
	content_mime_type?: 'text/markdown' | 'text/adf';
	experience_id?: string;
	cloud_id?: string;
	product_activation_id?: string;
	jira_activation_id?: string;
	user_org_id?: string;
	audio?: null;
	conversation_outcome?: null;
	orchestration_graphs?: null | unknown[];
	minion_outputs?: MinionOutputs;
	scenario?: {
		id: string;
		name: string;
		actions: string[];
	} | null;
	data_parts?: MessageDataPart[];
	insertable_sections?: InsertableSection[];
};

export type MessageDataPart = {
	data: unknown;
	metadata?: {
		mimeType?: string;
	};
	kind?: string;
};

export type StreamActionError = {
	type: string;
	message: {
		key: string;
		invocationId: string;
		actionId: string | null;
		// Producers are inconsistent: some emit `data.message`, some `data.content`, some neither.
		// Read via `getStreamActionErrorMessage` rather than reaching in directly. See AIFC-2057.
		data?: { message?: string; content?: string } | null;
		content?: string;
		status: string;
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	metadata?: any;
	millisOffset?: number;
};

export type AgentPlugins = {
	'Activity-Plugin': object;
	'Arithmetic-Plugin': object;
	'Content-Read-Plugin': object;
	'Jira-Issue-Create-Plugin': object;
	'Jira-JQL-Plugin': object;
	'Jira-Software-Plugin': object;
	'Next-Best-Task-Plugin': object;
	'Open-Toolchain-Plugin': object;
	'Profile-Plugin': object;
	'Search-QA-Plugin': {
		space_filter?: string[];
	};
	'Unified-Help-Plugin': object;
};

/* TODO: This will evolve as we enrich support for plugins, for the moment
we simply expect to make a direct replacement for `config.space_filters`
with `config.available_plugins['Your-Plugin'].space_filters`. Right now we only support
configuring one plugin, which is the Search-QA-Plugin, which can take the
space_filters configuration, per what our AgentForm expects

Related BE PR: https://bitbucket.org/atlassian/assistance-service/pull-requests/461/aim-367-allow-agents-to-use-plugin-config
*/
export type AgentAvailablePlugins = {
	[k in keyof AgentPlugins]?: AgentPlugins[k];
};

export type AgentKnowledgeSourceBase<T extends string, U = undefined> = {
	enabled: boolean;
	source: T;
	configuration: U;
};

export type ConfluenceKnowledgeSource = AgentKnowledgeSourceBase<
	'confluence',
	{
		space_filter: string[];
		parent_filter: string[];
	}
>;

export type JiraKnowledgeSource = AgentKnowledgeSourceBase<
	'jira',
	{
		project_filter: string[];
	}
>;

export type JsmKnowledgeSource = AgentKnowledgeSourceBase<
	'jsm',
	{
		project_filter: string[];
	}
>;

export type AtlasKnowledgeSource = AgentKnowledgeSourceBase<'atlas'>;
export type GoogleDriveKnowledgeSource = AgentKnowledgeSourceBase<'google_drive'>;
export type MicrosoftSharepointKnowledgeSource = AgentKnowledgeSourceBase<'microsoft_sharepoint'>;
export type SlackKnowledgeSource = AgentKnowledgeSourceBase<'slack'>;
export type LoomKnowledgeSource = AgentKnowledgeSourceBase<'loom'>;
export type OtherKnowledgeSource = AgentKnowledgeSourceBase<string>;

export type AgentKnowledgeSource =
	| ConfluenceKnowledgeSource
	| JiraKnowledgeSource
	| JsmKnowledgeSource
	| AtlasKnowledgeSource
	| GoogleDriveKnowledgeSource
	| MicrosoftSharepointKnowledgeSource
	| SlackKnowledgeSource
	| LoomKnowledgeSource
	| OtherKnowledgeSource;

export type AgentKnowledgeConfiguration = {
	// Known sources
	source:
		| 'confluence'
		| 'jira'
		| 'jsm'
		| 'atlas'
		| 'microsoft_sharepoint'
		| 'google_drive'
		| 'slack'
		| 'loom'
		| string;
	iconUrl: string;
	friendlyName: string;
	isUserOAuthed: boolean;
	authResolutionMode?: string;
	outboundAuthUrl: string;
	connectorCreatedAt: string | null;
	connectedAt: string | null;
	resourceType: string | null;
	providerId: string | null;
	datasourceId: string | null;
	connectorSyncType: string | null;
	cloudId?: string | null;
};

export type MappedAgentKnowledgeConfiguration = AgentKnowledgeConfiguration & {
	checkboxFieldName: string;
	actionSubjectId: string;
	checkboxLabel: string;
};

export type AgentKnowledgeConfigurationResponse = {
	knowledgeSources: AgentKnowledgeConfiguration[];
};

export type AgentScenarioKnowledgeSource = {
	enabled?: boolean;
	source: string;
	cloudId?: string | null;
	filters?: {
		parent_filter?: string[];
		space_filter?: string[];
		project_filter?: string[];
	};
};

export type AgentScenarioAction = {
	id: string;
	name?: string;
	displayName?: string;
	description?: string;
	tags?: string[];
	iconUrl?: string;
};

export type AgentScenario = {
	id: string;
	name: string;
	invocationDescription?: string;
	instructions?: string;
	knowledgeSources?: {
		enabled?: boolean;
		sources?: AgentScenarioKnowledgeSource[];
	};
	actions?: AgentScenarioAction[];
};

export type Agent = {
	id: string;
	identity_account_id?: string | null;
	named_id: string;
	name: string;
	description: string | null;
	system_prompt_template?: string | null;
	behaviour?: string | null;
	creator_type: 'SYSTEM' | 'CUSTOMER' | 'THIRD_PARTY' | 'FORGE' | 'OOTB' | 'REMOTE_A2A';
	creator?: string | null;
	available_plugins: AgentAvailablePlugins;
	visibility?: 'PUBLIC' | 'PRIVATE' | null;
	is_default: boolean;
	external_files?: ExternalFile[] | null;
	actor_type: 'AGENT';
	creator_cloud_id?: string | null;
	follow_up_prompt_template?: string | null;
	plugin_routing_type?: 'DEFAULT' | 'SKIP' | null;
	user_defined_conversation_starters: string[] | null;
	favourite: boolean;
	deactivated: boolean;
	deactivatedAt?: string;
	knowledge_sources?: {
		enabled: boolean;
		sources: AgentKnowledgeSource[];
	} | null;
	action_config?: AgentAction[] | null;
	external_config_reference?: string;
	icon?: string | null;
	authoring_team?: {
		id: string;
		profile_url: string;
		display_name: string;
		avatar_url?: string;
	} | null;
	is_verified?: boolean | null;
	agentAri?: string | null;
	execution_config?: {
		execution_tier?: 'TIER_0' | 'TIER_1' | 'TIER_2' | null;
	} | null;
};

export type AgentDetails = Agent & {
	favourite_count: number;
};

export type ConversationChannelDataMessage = {
	type: 'CONVERSATION_CHANNEL_DATA';
	assistant_message_id: string;
} & Pick<ConversationChannel, 'name' | 'time_created' | 'last_message_timestamp' | 'scopes'>;

/**
 * Live update of the conversation's scope list, emitted by the BE once
 * scope extraction completes for the current turn. Mirrors the existing
 * `ConversationChannelScope` shape so consumers can reuse the same type
 * already returned by `GET /conversations`. The BE omits this event when
 * the resolved scope set is unchanged (no-op guard).
 *
 * Ordering: arrives after `CONVERSATION_CHANNEL_DATA` and before
 * `FINAL_RESPONSE` on the SSE stream.
 */
export type ScopeDeterminedMessage = {
	type: 'SCOPE_DETERMINED';
	conversation_id: string;
	assistant_message_id?: string;
	scopes: ConversationChannelScope[];
	sequence_id?: string;
};

export type SpaceSourceRecommendation = {
	recommendationId: string;
	url: string;
	ari: string | null;
	sourceType: string;
	name: string | null;
};

export type SpaceSourceRecommendationsMessage = {
	type: 'SPACE_SOURCE_RECOMMENDATIONS';
	message: {
		space_id: string;
		recommendations: SpaceSourceRecommendation[];
	};
	sequence_id?: string;
};

export type ConversationStarterResponse = {
	conversation_starters: { text: string }[];
};

export type DynamicConversationStarterRequestNew =
	AiFeatureRequestEnvelopeRovoConvoStarterRecommendationRequestNew;

export type DynamicConversationStarterResponseNew =
	| AIFeatureResponseEnvelopeRovoConvoStarterRecommendationResponseNew
	| undefined;

export type FetchStreamReturn<T, isSSEFormatted extends boolean = false> = {
	responseData: {
		traceId?: string;
	};
	generator: isSSEFormatted extends true
		? AsyncGenerator<ParsedSSEMessage<T>> // SSE: always ParsedSSEMessage<T>
		: AsyncGenerator<T>; // Non-SSE: clean type, no check needed
};
